import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, TextField, Grid, Button, 
  MenuItem, Select, Divider, ToggleButtonGroup, 
  ToggleButton, Paper, IconButton, Tooltip, Stack, 
  Fade, Switch, FormControlLabel, Dialog, DialogTitle, 
  DialogContent, DialogContentText, DialogActions,
  CircularProgress, Snackbar, Alert, Chip
} from '@mui/material';
import { 
  Save, CloudDone, Construction, History, 
  WarningAmber, Lock, LockOpen, CheckCircle, Person,
  Https, VerifiedUser
} from '@mui/icons-material';


export const GatewaySettings = ({ organization, initialConfigs, onRefresh }) => {
  const [env, setEnv] = useState('PRODUCTION');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ open: false, text: '', severity: 'success' });
  
  const [availableVersions, setAvailableVersions] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [configs, setConfigs] = useState(initialConfigs);

  // ─── SSL STATE ───
  const [sslExpiry, setSslExpiry] = useState(null);

  useEffect(() => {
    setConfigs(initialConfigs);
  }, [initialConfigs]);

  const fetchSslStatus = useCallback(async () => {
    const targetId = organization.cid || organization.id;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/customers/${targetId}/ssl/expiring`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setSslExpiry(data[0].expiration_date);
      }
    } catch (err) {
      console.error("SSL fetch error:", err);
    }
  }, [organization]);

  const fetchVersions = useCallback(async () => {
    setVersionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const customer_type = organization?.customer_type?.toUpperCase() || 'FUEL';
      const response = await fetch(`https://console.dreamtec.io/app-versions?customer_type=${customer_type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvailableVersions(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); } 
    finally { setVersionLoading(false); }
  }, [organization]);

  useEffect(() => { 
    fetchVersions(); 
    fetchSslStatus();
  }, [fetchVersions, fetchSslStatus]);

  // ─── SSL INFO RENDERER ───
  const renderSSLInfo = () => {
    if (!sslExpiry) return null;
    
    const expiryDate = new Date(sslExpiry);
    const now = new Date();
    const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    let statusColor = '#10b981'; // Green
    if (daysRemaining <= 7) statusColor = '#ef4444'; // Red
    else if (daysRemaining <= 30) statusColor = '#f59e0b'; // Orange

    return (
      <Box sx={{ mt: 1.5, pl: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <VerifiedUser sx={{ fontSize: 16, color: statusColor, mt: 0.2 }} />
          <Box>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#1A3A3F', lineHeight: 1.2, letterSpacing: '0.02rem' }}>
              CERT MANAGED BY: CENTRAL CONSOLE
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: statusColor }}>
              {daysRemaining <= 0 ? 'Expired' : `Expiring in ${daysRemaining} days`}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  };

  const handleEnvChange = (e, newEnv) => { 
    if (newEnv !== null) {
      setEnv(newEnv);
      setIsEditMode(false); 
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfigs(prev => ({
      ...prev,
      [env]: { ...prev[env], [name]: value }
    }));
  };

  const saveConfig = async (payload, successMessage) => {
    try {
      const token = localStorage.getItem('token');
      const targetId = organization.cid || organization.id;
      const response = await fetch(`https://console.dreamtec.io/customers/${targetId}/configs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Update failed');
      setStatusMsg({ open: true, text: successMessage, severity: 'success' });
      setIsEditMode(false);
      setIsConfirmOpen(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setStatusMsg({ open: true, text: err.message, severity: 'error' });
    }
  };

  const handleInfraSave = () => {
    const original = initialConfigs[env];
    const updated = configs[env];
    const infraFields = ['protocol', 'domain_name', 'server_port', 'android_slug', 'web_frontend_slug', 'api_backend_slug'];
    const payload = { environment: env };
    let hasChanges = false;
    infraFields.forEach(field => {
      if (String(updated[field]) !== String(original[field])) {
        payload[field] = updated[field];
        hasChanges = true;
      }
    });
    if (!hasChanges) {
      setStatusMsg({ open: true, text: "No infrastructure changes detected", severity: 'info' });
      setIsEditMode(false);
      setIsConfirmOpen(false);
      return;
    }
    saveConfig(payload, `${env} network parameters updated`);
  };

  const handleAppPush = () => {
    const original = initialConfigs[env];
    const updated = configs[env];
    if (updated.current_app_version === original.current_app_version) {
      setStatusMsg({ open: true, text: "Build version is already up to date", severity: 'info' });
      return;
    }
    const payload = { environment: env, current_app_version: updated.current_app_version };
    saveConfig(payload, `App Build v${updated.current_app_version} deployed to ${env}`);
  };

  const current = configs ? configs[env] : null;
  if (!current) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#2F727C' }} /></Box>;

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      
      {/* ─── GLOBAL HEADER ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1A3A3F', letterSpacing: -0.5 }}>
            Network Gateway
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={env} exclusive onChange={handleEnvChange}
          sx={{ 
            bgcolor: '#f1f5f9', borderRadius: '12px', p: 0.5, border: '1px solid #e2e8f0', height: 44,
            '& .MuiToggleButton-root': { border: 'none', borderRadius: '8px !important', px: 3, fontSize: '0.75rem', fontWeight: 800, color: '#64748B', textTransform: 'none' },
            '& .Mui-selected': { bgcolor: '#fff !important', color: '#2F727C !important', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
          }}
        >
          <ToggleButton value="PRODUCTION"><CloudDone sx={{ mr: 1, fontSize: 18 }} /> Production</ToggleButton>
          <ToggleButton value="TEST"><Construction sx={{ mr: 1, fontSize: 18 }} /> Test</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Grid container spacing={3} alignItems="stretch">
        {/* LEFT: CONFIGURATION */}
        <Grid item xs={12} md={8}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, px: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1A3A3F' }}>ENDPOINT CONFIGURATION</Typography>
            <FormControlLabel
              control={<Switch size="small" checked={isEditMode} onChange={(e) => setIsEditMode(e.target.checked)} color="warning" />}
              label={<Typography variant="caption" sx={{ fontWeight: 800, color: isEditMode ? '#f57c00' : '#94a3b8' }}>EDIT MODE</Typography>}
            />
          </Stack>

          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: isEditMode ? '#fff' : 'rgba(248, 250, 252, 0.5)' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', mb: 1, display: 'block' }}>PROTOCOL</Typography>
                <Select fullWidth size="small" name="protocol" value={current.protocol || 'http'} onChange={handleChange} disabled={!isEditMode}
                  sx={{ borderRadius: 2, bgcolor: isEditMode ? '#fff' : 'transparent' }}
                >
                  <MenuItem value="https">https://</MenuItem>
                  <MenuItem value="http">http://</MenuItem>
                </Select>

                {/* ─── NEW SSL INFO PLACEMENT ─── */}
                {renderSSLInfo()}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', mb: 1, display: 'block' }}>DOMAIN</Typography>
                <TextField fullWidth size="small" name="domain_name" value={current.domain_name || ''} onChange={handleChange} disabled={!isEditMode}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isEditMode ? '#fff' : 'transparent' } }} 
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', mb: 1, display: 'block' }}>PORT</Typography>
                <TextField fullWidth size="small" name="server_port" value={current.server_port || ''} onChange={handleChange} disabled={!isEditMode}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isEditMode ? '#fff' : 'transparent' } }} 
                />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12} md={4}><Typography variant="caption" sx={{ fontWeight: 800, mb: 1, display: 'block' }}>ANDROID API</Typography><TextField fullWidth size="small" name="android_slug" value={current.android_slug || ''} onChange={handleChange} disabled={!isEditMode} /></Grid>
              <Grid item xs={12} md={4}><Typography variant="caption" sx={{ fontWeight: 800, mb: 1, display: 'block' }}>WEBODV</Typography><TextField fullWidth size="small" name="web_frontend_slug" value={current.web_frontend_slug || ''} onChange={handleChange} disabled={!isEditMode} /></Grid>
              <Grid item xs={12} md={4}><Typography variant="caption" sx={{ fontWeight: 800, mb: 1, display: 'block' }}>BACKEND SERVER</Typography><TextField fullWidth size="small" name="api_backend_slug" value={current.api_backend_slug || ''} onChange={handleChange} disabled={!isEditMode} /></Grid>
              
              <Fade in={isEditMode}>
                <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                   <Button variant="contained" onClick={() => setIsConfirmOpen(true)} sx={{ bgcolor: '#2F727C', fontWeight: 800, borderRadius: 2 }}>Apply Changes</Button>
                </Grid>
              </Fade>
            </Grid>
          </Paper>
        </Grid>

        {/* RIGHT: APP RELEASE */}
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 1.5, px: 1, minHeight: '30px', display: 'flex', alignItems: 'center' }}>
             <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1A3A3F' }}>APP RELEASE</Typography>
          </Box>
          <Paper elevation={0} sx={{ 
            p: 4, borderRadius: 2, background: env === 'PRODUCTION' ? '#bfe3e8' : '#fcc433',
            color: '#1A3A3F', display: 'flex', flexDirection: 'column', justifyContent: 'center'
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, opacity: 0.8 }}>
              <History fontSize="small" /><Typography variant="caption" sx={{ fontWeight: 800 }}>DEPLOYED BUILD</Typography>
            </Stack>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 3 }}>{current.current_app_version || 'N/A'}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, mb: 1, display: 'block', opacity: 0.8 }}>UPGRADE TARGET</Typography>
            <Select fullWidth size="small" name="current_app_version" value={current.current_app_version || ''} onChange={handleChange} disabled={versionLoading}
              sx={{ bgcolor: 'rgba(255,255,255,0.35)', color: 'inherit', borderRadius: 2, fontWeight: 700, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
            >
              {availableVersions.map(v => <MenuItem key={v.version_label} value={v.version_label}>v{v.version_label}</MenuItem>)}
            </Select>
            <Button color="primary" variant="contained" fullWidth onClick={handleAppPush} sx={{ mt: 4, py: 1.5, borderRadius: 2, fontWeight: 900 }}>PUSH BUILD</Button>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Update</DialogTitle>
        <DialogContent><DialogContentText>Update network parameters for <strong>{env}</strong>?</DialogContentText></DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setIsConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleInfraSave} variant="contained" sx={{ bgcolor: '#2F727C', fontWeight: 800 }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={statusMsg.open} autoHideDuration={4000} onClose={() => setStatusMsg({ ...statusMsg, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={statusMsg.severity} sx={{ fontWeight: 700, borderRadius: 2 }}>{statusMsg.text}</Alert>
      </Snackbar>

      {/* FOOTER AUDIT */}
      <Box sx={{ mt: 4, px: 1, display: 'flex', justifyContent: 'right' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Person sx={{ fontSize: 18, color: '#2F727C' }} />
          <Typography sx={{ fontSize: '0.85rem', color: '#5d7e83' }}>
            Last updated by <Box component="span" sx={{ fontWeight: 800, color: '#1A3A3F' }}>{current.updated_by || 'System'}</Box>
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>
            • {current.last_updated ? new Date(current.last_updated).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'Never'}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};